/** @jsxImportSource @emotion/react */

import React, { useEffect, useRef, useState } from 'react';
import { getAttendanceType } from '@util/attendance';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import {
  EApiAttendanceType,
  EApiLanguageCode,
  EApiStudyType,
  type IClassroomExpressionProblem,
} from '@datatype/iyagigo/api-type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import ImageAudioButton from '@design-system/image-audio-button';
import BottomContainer from '@component/common/architecture/bottom-container';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import Gap from '@component/common/gap';
import OtterTeacher, { type IPlaybackState } from '@component/common/otter-teacher';
import RemotionWatchBoard from '@component/common/remotion-watch-board';
import WatchControl from '@component/iyagigo/classroom/grammar-study/lecture-controls';
import TextBubble from '@design-system/text-bubble';
import IconBlock from '@component/common/block/icon-block';
import {
  useModifyClassroomExpressionWorkbookMutation,
  useClassroomExpressionWorkbooksQuery,
} from '@hook/iyagigo/useApi';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import { usePlaySound } from '@hook/common/useSound';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { actions as modalActions } from '@store/slice/modal-slice';
import { shuffleArrayWithSeed } from '@util/random';
import { addFurigana, createFuriganaMap, normalizeExplanation } from '@util/string';

type TStudyPhase = 'watch' | 'read' | 'practice';

const toBubbleFurigana = (text: string, pronunciation?: string | null) => {
  if (!pronunciation) return text;

  const furiganaMap = createFuriganaMap(text, pronunciation);
  Array.from(furiganaMap.keys()).forEach((notation) => {
    if (/^\d+$/.test(notation)) furiganaMap.delete(notation);
  });

  return addFurigana(text, furiganaMap, 'start');
};

const getSpeakerProfileEmoji = (gender?: string | null) => {
  if (gender === '남' || gender === 'male') return '🧑🏻';
  if (gender === '여' || gender === 'female') return '👩🏻';
  return '🙂';
};

interface IProps {
  onProgress?: React.Dispatch<{ current: number; total: number }>;
  onTitleChange?: React.Dispatch<string>;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  width: 100%;
`;

const WatchContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-flow: column nowrap;
  width: 100%;
  overflow: hidden;
  border-radius: ${size.MARGIN_2};
`;

const TeacherContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const NavigationContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ControlRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
  height: 35px; // medium TextButton 높이
`;

const NAVIGATION_BUTTON_WIDTH = '80px';

const ReadContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
`;

const ExpressionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
`;

const ExpressionTitleContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const NumberTable = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const NumberCell = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.GRAY_3};
`;

const NumberExpressionContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const PracticeContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const SituationContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`;

const ConversationContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  width: 100%;
  min-height: 230px;
  padding: ${size.MARGIN_2};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_5};
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  width: 100%;
`;

const ChoiceContainer = styled.button<{ $borderColor: string; $answered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 5px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
  color: ${color.BLACK_2};
  cursor: ${({ $answered }) => ($answered ? 'default' : 'pointer')};
`;

const BubbleControlContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

const BubbleControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
  margin-top: 5px;
`;

const BubbleTranslationContainer = styled.div`
  width: 100%;
  margin-top: 4px;
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
`;

const HiddenAudio = styled.audio`
  display: none;
`;

interface IBubbleControlProps {
  audioLink: string | null;
  translation: string;
  isUser: boolean;
  isTranslationVisibleByDefault?: boolean;
}

const BubbleControl: React.FC<IBubbleControlProps> = ({
  audioLink,
  translation,
  isUser,
  isTranslationVisibleByDefault = false,
}) => {
  const [isTranslationVisible, setIsTranslationVisible] = useState(isTranslationVisibleByDefault);
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick } = useAudioPlayer({
    sources: audioLink ? [audioLink] : [],
  });

  return (
    <BubbleControlContainer $isUser={isUser}>
      <BubbleControlRow>
        {audioLink && (
          <>
            <IconBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handlePlayAndPauseClick }}
            />
            <HiddenAudio
              ref={(element) => {
                if (element) audioRefs.current[0] = element;
              }}
              src={audioLink}
              onEnded={handlePlayCompleted}
            />
          </>
        )}
        {translation && (
          <IconBlock
            icon={isTranslationVisible ? 'text-snippet-off' : 'text-snippet'}
            design={{
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: () => setIsTranslationVisible((visible) => !visible) }}
          />
        )}
      </BubbleControlRow>
      {isTranslationVisible && translation && (
        <BubbleTranslationContainer>
          <TextBlock
            text={translation}
            design={{
              text: { size: 'small', color: color.BLACK_2 },
              box: { alignX: 'left' },
            }}
          />
        </BubbleTranslationContainer>
      )}
    </BubbleControlContainer>
  );
};

const getChoiceBorderColor = (isAnswerView: boolean, isSelected: boolean, isCorrect: boolean): string => {
  if (isAnswerView) {
    if (isCorrect) return color.GREEN_4;
    if (isSelected) return color.RED_3;
  } else if (isSelected) {
    return color.YELLOW_3;
  }
  return color.GRAY_4;
};

const ClassroomExpressionStudy: React.FC<IProps> = ({ onProgress, onTitleChange }) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const playSound = usePlaySound();
  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const expressionKey = new URLSearchParams(location.search).get('subject');
  const studyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const isEnglish = studyLanguage === EApiLanguageCode.ENGLISH;
  const { data: workbooksResponse, isLoading } = useClassroomExpressionWorkbooksQuery(studyLanguage);
  const modifyWorkbookMutation = useModifyClassroomExpressionWorkbookMutation(studyLanguage);
  const workbooks = workbooksResponse?.data ?? [];
  const expressions = workbooks.map((workbook) => workbook.basicExpression);
  const expressionIndex = expressions.findIndex((item) => item.expressionKey === expressionKey);
  const expression = expressions[expressionIndex] ?? null;
  const workbook = workbooks.find((item) => item.basicExpression.expressionKey === expressionKey);

  const [phase, setPhase] = useState<TStudyPhase>('watch');
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true, phase === 'watch' ? 60_000 : 10_000);
  const [problemIndex, setProblemIndex] = useState(0);
  const [selectedChoiceKey, setSelectedChoiceKey] = useState<string | null>(null);
  const [isAnswerView, setIsAnswerView] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const choiceShuffleSeedRef = useRef(Math.random().toString(36));
  const [watchPlayback, setWatchPlayback] = useState<IPlaybackState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    playbackRate: 1,
  });
  const watchAudioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (phase !== 'watch') return;
    const timer = window.setInterval(() => {
      const studyTime = getActiveTime();
      if (!studentKey || studyTime <= 0) return;
      recordStudy({
        studentKey,
        studyType: EApiStudyType.CLASSROOM_EXPRESSION,
        languageCode: studyLanguage,
        studyTime,
      });
      resetActiveTime(false);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [phase, studentKey, studyLanguage, getActiveTime, resetActiveTime, recordStudy]);

  const readExpressions = expression?.read?.expression ?? [];
  const problems: IClassroomExpressionProblem[] = expression?.practice?.problem ?? [];
  const currentProblem = problems[problemIndex] ?? null;
  const currentLines = currentProblem?.line ?? [];
  const currentChoices = currentProblem
    ? shuffleArrayWithSeed(currentProblem.choice, `${choiceShuffleSeedRef.current}-${currentProblem.key}`)
    : [];
  const hasWatch = Boolean(expression?.watch?.audioLink);
  const hasRead = readExpressions.length > 0;
  const studyStepCount = (hasWatch ? 1 : 0) + (hasRead ? 1 : 0);
  const totalSteps = studyStepCount + problems.length;
  const currentStep = phase === 'watch' ? 0 : phase === 'read' ? (hasWatch ? 1 : 0) : studyStepCount + problemIndex;

  const watch = expression?.watch ?? null;

  const syncWatchPlayback = () => {
    const audio = watchAudioRef.current;
    if (!audio) return;
    setWatchPlayback({
      currentTime: audio.currentTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      isPlaying: !audio.paused,
      playbackRate: audio.playbackRate,
    });
  };

  useEffect(() => {
    onProgress?.({ current: currentStep, total: totalSteps });
  }, [currentStep, onProgress, totalSteps]);

  useEffect(() => {
    if (expression?.expressionName?.ko) onTitleChange?.(expression.expressionName.ko);
  }, [expression, onTitleChange]);

  useEffect(() => {
    if (!isLoading && !expression) navigate(IYAGIGO_PATH.CLASSROOM_HOME_PATH, { replace: true });
  }, [expression, isLoading, navigate]);

  useEffect(() => {
    if (!isLoading && expression && !hasWatch && phase === 'watch') {
      setPhase(hasRead ? 'read' : 'practice');
    }
  }, [expression, hasRead, hasWatch, isLoading, phase]);

  if (!expression) return null;

  const showCompleteModal = () => {
    if (workbook && !workbook.studiedAt) {
      modifyWorkbookMutation.mutate({ workbookKey: workbook.key, isStudy: true });
    }
    checkAttendance(getAttendanceType(null, 'expression', 'classroom'));
    const incorrectCount = problems.length - correctCount;
    dispatch(
      modalActions.showContentModal({
        type: 'study-celebration',
        data: {
          title: '오늘도 해냈어요!',
          description: `<strong>${expression.expressionName?.ko ?? ''}</strong> 학습을 끝까지 마쳤어요`,
        },
      }),
    );
  };

  const handleOpenRead = () => {
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_EXPRESSION,
      languageCode: studyLanguage,
      studyCount: 1,
    });
    resetActiveTime();
    watchAudioRef.current?.pause();
    if (hasRead) {
      setPhase('read');
    } else if (problems.length > 0) {
      setPhase('practice');
    } else {
      showCompleteModal();
    }
  };

  const handleOpenWatch = () => {
    if (watchAudioRef.current) watchAudioRef.current.currentTime = 0;
    setPhase('watch');
  };

  const handleStartPractice = () => {
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_EXPRESSION,
      languageCode: studyLanguage,
      studyTime: getActiveTime(),
    });
    resetActiveTime();
    if (problems.length === 0) {
      showCompleteModal();
      return;
    }
    setPhase('practice');
  };

  const handleScoring = () => {
    if (!currentProblem || selectedChoiceKey === null || isAnswerView) return;
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CLASSROOM_EXPRESSION,
      languageCode: studyLanguage,
      studyTime: getActiveTime(),
    });
    resetActiveTime();
    const selectedChoice = currentChoices.find((choice) => choice.key === selectedChoiceKey);
    const isCorrect = selectedChoice?.correct ?? false;
    setIsAnswerView(true);
    playSound(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) setCorrectCount((count) => count + 1);
  };

  const handleNextProblem = () => {
    if (problemIndex >= problems.length - 1) {
      showCompleteModal();
      return;
    }
    setProblemIndex((index) => index + 1);
    setSelectedChoiceKey(null);
    setIsAnswerView(false);
  };

  const NumberCells = readExpressions.map((item) => (
    <NumberCell key={`${item.notation}-${item.pronunciation ?? ''}`}>
      <NumberExpressionContainer>
        <TextBlock
          text={item.notation}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { width: '60px', alignX: 'left' } }}
        />
        <TextBlock
          text={isEnglish ? '' : (item.pronunciation ?? '')}
          design={{ text: { size: 'small', color: color.BLACK_2 }, box: { width: 'fill', alignX: 'left' } }}
        />
      </NumberExpressionContainer>
      {item.audioLink && (
        <ImageAudioButton
          sources={[item.audioLink]}
          theme={ETheme.GRAY}
          tone='normal'
          kind='outlined'
          size='small'
          shape='round'
          width='35px'
        />
      )}
    </NumberCell>
  ));

  const ExpressionCards = readExpressions.map((item) => (
    <Canvas key={`${item.notation}-${item.pronunciation ?? ''}`} theme={ETheme.GRAY} shape='round' width='100%'>
      <ExpressionTitleContainer>
        <ExpressionContainer>
          <TextBlock
            text={item.notation}
            annotation={
              !isEnglish && item.pronunciation && item.pronunciation !== item.notation
                ? { reading: item.pronunciation }
                : undefined
            }
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <TextBlock
            text={item.translation?.ko ?? ''}
            design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
          />
        </ExpressionContainer>
        {item.audioLink && (
          <ImageAudioButton
            sources={[item.audioLink]}
            theme={ETheme.GRAY}
            tone='normal'
            kind='outlined'
            size='small'
            shape='round'
            width='35px'
          />
        )}
      </ExpressionTitleContainer>
    </Canvas>
  ));

  const Choices = currentChoices.map((choice) => {
    const isSelected = selectedChoiceKey === choice.key;
    return (
      <ChoiceContainer
        key={choice.key}
        type='button'
        $borderColor={getChoiceBorderColor(isAnswerView, isSelected, choice.correct)}
        $answered={isAnswerView}
        onClick={() => {
          if (!isAnswerView) setSelectedChoiceKey(choice.key);
        }}>
        <TextBlock
          text={choice.notation}
          annotation={
            !isEnglish && choice.pronunciation && choice.pronunciation !== choice.notation
              ? { reading: choice.pronunciation }
              : undefined
          }
          design={{ text: { size: 'large', color: color.BLACK_2 }, box: { alignX: 'left', width: '100%' } }}
        />
      </ChoiceContainer>
    );
  });

  const selectedChoice = currentChoices.find((choice) => choice.key === selectedChoiceKey);
  const correctChoice = currentChoices.find((choice) => choice.correct);
  const findChoiceTranslation = (notation?: string) =>
    expressions
      .flatMap((item) => item.read?.expression ?? [])
      .find((item) => item.notation === notation || item.pronunciation === notation)?.translation?.ko ?? '';
  const correctChoiceTranslation = findChoiceTranslation(correctChoice?.notation);
  const isSelectedCorrect = selectedChoice?.correct ?? false;
  const isLastProblem = problemIndex === problems.length - 1;
  const answerSpeaker = currentProblem?.speaker?.find((speaker) => speaker.name === '나');
  const AnswerBubble = (
    <TextBubble
      type='plain'
      text={
        isAnswerView && correctChoice
          ? isEnglish
            ? correctChoice.notation
            : toBubbleFurigana(correctChoice.notation, correctChoice.pronunciation)
          : '빈칸을 채워 주세요'
      }
      animatedText={!isAnswerView}
      theme={ETheme.BROWN}
      size='medium'
      align='right'
      speakerName={answerSpeaker?.name ?? '나'}
      profileEmoji={getSpeakerProfileEmoji(answerSpeaker?.gender)}
      lineMaxWidth='calc(100% - 50px)'
      control={
        isAnswerView && correctChoice ? (
          <BubbleControl
            audioLink={currentProblem?.audioLink ?? null}
            translation={correctChoiceTranslation}
            isUser
            isTranslationVisibleByDefault
          />
        ) : undefined
      }
    />
  );
  const answerIdx = currentProblem ? Math.min(Math.max(currentProblem.answerIdx ?? 0, 0), currentLines.length) : 0;
  const ConversationBubbles = currentProblem
    ? Array.from({ length: currentLines.length + 1 }, (_, index) => {
        if (index === answerIdx) {
          return <React.Fragment key={`${currentProblem.key}-answer`}>{AnswerBubble}</React.Fragment>;
        }

        const lineIdx = index < answerIdx ? index : index - 1;
        const line = currentLines[lineIdx];
        const speaker = currentProblem.speaker?.find((item) => item.id === line.speaker);
        const isUser = speaker?.name === '나';
        return (
          <TextBubble
            key={`${currentProblem.key}-line-${lineIdx}`}
            type='plain'
            text={isEnglish ? line.text : toBubbleFurigana(line.text, line.pronunciation)}
            theme={isUser ? ETheme.BROWN : ETheme.GRAY}
            size='medium'
            align={isUser ? 'right' : 'left'}
            speakerName={speaker?.name ?? line.speaker}
            profileEmoji={getSpeakerProfileEmoji(speaker?.gender)}
            lineMaxWidth='calc(100% - 50px)'
            control={
              <BubbleControl audioLink={line.audioLink} translation={line.translation?.ko ?? ''} isUser={isUser} />
            }
          />
        );
      })
    : null;

  return (
    <Wrapper>
      <ScrollContainer>
        <Scroll key={phase} direction='y' height='100%'>
          <ContentContainer>
            {phase === 'watch' && watch?.audioLink && (
              <>
                {/* Web Audio 립싱크 복원 시 crossOrigin='anonymous'와 서버 CORS 허용을 함께 검토한다. */}
                <audio
                  ref={watchAudioRef}
                  src={watch.audioLink}
                  preload='metadata'
                  style={{ display: 'none' }}
                  onLoadedMetadata={syncWatchPlayback}
                  onTimeUpdate={syncWatchPlayback}
                  onPlay={syncWatchPlayback}
                  onPause={syncWatchPlayback}
                  onRateChange={syncWatchPlayback}
                  onEnded={syncWatchPlayback}
                />
                <WatchContainer>
                  <TeacherContainer>
                    <OtterTeacher
                      audioRef={watchAudioRef}
                      onClick={() => {
                        const audio = watchAudioRef.current;
                        if (!audio) return;
                        if (audio.paused) void audio.play().catch(() => undefined);
                        else audio.pause();
                      }}
                    />
                  </TeacherContainer>
                  <RemotionWatchBoard
                    class='expression'
                    watch={watch}
                    playback={watchPlayback}
                    contentKey={expression.expressionKey}
                  />
                </WatchContainer>
              </>
            )}

            {phase === 'read' && (
              <ReadContainer>
                <Canvas theme={ETheme.BROWN} shape='round' width='100%'>
                  <ExpressionContainer>
                    <Chip
                      text={expression.expressionName?.ko ?? ''}
                      size='small'
                      theme={ETheme.BROWN}
                      shape='round'
                      usage='static'
                      kind='filled'
                      width='fit-content'
                    />
                    <TextBlock
                      text='이렇게 말해요'
                      design={{ text: { size: 'medium', color: color.BROWN_2, bold: true } }}
                    />
                    <TextBlock
                      text={normalizeExplanation(expression.description?.ko ?? undefined, color.BLACK_2)}
                      design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
                    />
                    {expression.read?.note?.ko && (
                      <>
                        <Gap height={size.MARGIN_1} />
                        <TextBlock
                          text='알아두기'
                          design={{ text: { size: 'medium', color: color.BROWN_2, bold: true } }}
                        />
                        <TextBlock
                          text={normalizeExplanation(expression.read.note.ko, color.BLACK_4)}
                          design={{ text: { size: 'small', color: color.BLACK_4 }, box: { alignX: 'left' } }}
                        />
                      </>
                    )}
                  </ExpressionContainer>
                </Canvas>
                <Gap height={size.MARGIN_2} />
                {expression.groupKey === 'number' ? (
                  <NumberTable>{NumberCells}</NumberTable>
                ) : (
                  <ExpressionContainer>{ExpressionCards}</ExpressionContainer>
                )}
              </ReadContainer>
            )}

            {phase === 'practice' && currentProblem && (
              <PracticeContainer>
                <ConversationContainer>
                  <SituationContainer>
                    <Chip
                      text={currentProblem.situation}
                      size='small'
                      theme={ETheme.BROWN}
                      shape='round'
                      usage='static'
                      kind='filled'
                      width='100%'
                    />
                  </SituationContainer>
                  {ConversationBubbles}
                </ConversationContainer>
              </PracticeContainer>
            )}
          </ContentContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        height='auto'
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={phase === 'practice' ? (isAnswerView ? 'answer' : 'question') : undefined}>
        <BottomContentContainer>
          {phase === 'watch' && watch?.audioLink && (
            <>
              <WatchControl audioRef={watchAudioRef} playback={watchPlayback} />
              <TextButton
                text='다음으로'
                theme={ETheme.BROWN}
                size='medium'
                shape='pill'
                width='100%'
                handleClick={handleOpenRead}
              />
            </>
          )}

          {phase === 'read' && (
            <NavigationContainer>
              <TextButton
                text={problems.length > 0 ? '연습하기' : '학습 완료'}
                theme={ETheme.BROWN}
                size='medium'
                shape='round'
                width={hasWatch ? 'calc(100% - 90px)' : '100%'}
                handleClick={handleStartPractice}
              />
              {hasWatch && (
                <TextButton
                  text='이전으로'
                  theme={ETheme.BROWN}
                  kind='outlined'
                  size='medium'
                  shape='square'
                  width='80px'
                  handleClick={handleOpenWatch}
                />
              )}
            </NavigationContainer>
          )}

          {phase === 'practice' && (
            <>
              <ChoicesContainer>{Choices}</ChoicesContainer>
              <ControlRowContainer>
                {isAnswerView && (
                  <TextButton
                    text={isLastProblem ? '끝내기' : '다음 문제'}
                    theme={ETheme.BROWN}
                    size='medium'
                    shape='square'
                    width={NAVIGATION_BUTTON_WIDTH}
                    handleClick={handleNextProblem}
                  />
                )}
              </ControlRowContainer>
              {isAnswerView ? (
                <TextButton
                  text={isSelectedCorrect ? '정답이에요' : '오답이에요'}
                  icon={isSelectedCorrect ? 'circle' : 'close'}
                  theme={isSelectedCorrect ? ETheme.GREEN : ETheme.RED}
                  size='medium'
                  shape='pill'
                  width='100%'
                />
              ) : (
                <TextButton
                  text='채점하기'
                  theme={ETheme.BROWN}
                  size='medium'
                  shape='pill'
                  width='100%'
                  disabled={!selectedChoiceKey}
                  handleClick={handleScoring}
                />
              )}
            </>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default ClassroomExpressionStudy;
